import "./styles.css";
import { useCallback, useEffect, useRef } from "react";
import {
  formatTime,
  formatTimeRange,
  isBlank,
  isEmptyList,
  isNullOrUndefined,
} from "../../utils";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import { tooltipLightStyles } from "../../styleUtils";
import { Fade, Tooltip } from "@mui/material";
import { TIME_CHART_ICONS, TIME_CHART_SIZE } from "./timeChartUtils";
import { ReactComponent as Arrival } from "../../assets/turnarounds/arrival.svg";
import { ReactComponent as Departure } from "../../assets/turnarounds/departure.svg";

function TimeChart(props) {
  const {
    size,
    columnTitle,
    timeChartItems,
    airportTimezone,
    timelineRanges,
    searchTerm,
    itemClickHandler,
    itemBlockClickHandler,
  } = props;
  const { t } = useTranslation();
  const mNow = moment().tz(airportTimezone);

  const containerRef = useRef(null);
  const markersRef = useRef(null);
  const gridlinesRef = useRef(null);
  const sidePanelRef = useRef(null);
  const sidePanelHeaderRef = useRef(null);

  // 1hr increments
  const timeBuckets = [];
  const timeInterval = moment(timelineRanges.mRangeStart);
  while (!timeInterval.isAfter(timelineRanges.mRangeEnd)) {
    if (timeInterval.isAfter(timelineRanges.mRangeStart)) {
      timeBuckets.push(moment(timeInterval));
    }
    timeInterval.add(1, "hour");
  }

  function getTimeAsPixels(minutes) {
    const containerEl = gridlinesRef.current;
    const containerWidth = !isNullOrUndefined(containerEl)
      ? containerEl.getBoundingClientRect().width - 2
      : 0;
    const totalWidth = containerWidth;

    const totalDuration = timelineRanges.mRangeEnd.diff(
      timelineRanges.mRangeStart,
      "minutes"
    );
    return minutes * (totalWidth / totalDuration);
  }

  function getTimelineMarkerStyle(mMakerTime) {
    if (isNullOrUndefined(mMakerTime)) return {};
    const durationFromRangeStart = mMakerTime.diff(
      timelineRanges.mRangeStart,
      "minutes"
    );
    const timelineLeft = getTimeAsPixels(durationFromRangeStart);
    return {
      left: `${timelineLeft}px`,
    };
  }

  function getTimelineBarStyle(taskInfo) {
    const mTaskStart = moment(taskInfo.startTime).tz(airportTimezone);
    const mTaskEnd = moment(taskInfo.endTime).tz(airportTimezone);
    const isBeforeRangeStart = mTaskStart.isBefore(timelineRanges.mRangeStart);
    const isAfterRangeEnd = mTaskEnd.isAfter(timelineRanges.mRangeEnd);
    const durationFromRangeStart = !isBeforeRangeStart
      ? mTaskStart.diff(timelineRanges.mRangeStart, "minutes")
      : 0;

    // Amount of the task that is trimmed off from the front
    const trimmedDurationFromStart = isBeforeRangeStart
      ? timelineRanges.mRangeStart.diff(mTaskStart, "minutes")
      : 0;
    const durationToRangeEnd = !isNullOrUndefined(mTaskStart)
      ? timelineRanges.mRangeEnd.diff(mTaskStart, "minutes")
      : 0;
    const adjustedDuration = isBeforeRangeStart
      ? taskInfo.duration - trimmedDurationFromStart
      : taskInfo.duration;
    const duration = isAfterRangeEnd
      ? durationToRangeEnd - trimmedDurationFromStart
      : adjustedDuration;
    const timelineWidth = getTimeAsPixels(duration);
    const timelineLeft = getTimeAsPixels(durationFromRangeStart);
    let style = {
      left: `${timelineLeft}px`,
    };
    if (duration > 0) {
      style.width = `${timelineWidth}px`;
    }
    if (isBeforeRangeStart) {
      style.borderTopLeftRadius = "0px";
      style.borderBottomLeftRadius = "0px";
    }
    if (isAfterRangeEnd) {
      style.borderTopRightRadius = "0px";
      style.borderBottomRightRadius = "0px";
    }
    return style;
  }

  useEffect(() => {
    // Reset scroll
    const scrollEl = containerRef?.current;
    if (!isNullOrUndefined(scrollEl)) {
      scrollEl.scrollTo(0, 0);
    }
  }, [searchTerm]);

  // Need to correct the vertical position of the label so that it doesn't get covered by header
  const handleContentScroll = useCallback(() => {
    const scrollEl = containerRef.current;
    const markersEl = markersRef.current;
    if (isNullOrUndefined(scrollEl) || isNullOrUndefined(markersEl)) return;

    const scrollTop = scrollEl.scrollTop;
    const scrollLeft = scrollEl.scrollLeft;
    markersEl.style.top = `${scrollTop}px`;

    // Keep sidepanel on left
    const sidePanelEl = sidePanelRef.current;
    const sidePanelHeaderEl = sidePanelHeaderRef.current;

    if (!isNullOrUndefined(sidePanelEl)) {
      sidePanelEl.style.left = `${scrollLeft}px`;
    }
    if (!isNullOrUndefined(sidePanelHeaderEl)) {
      // sidePanelHeaderEl.style.top = `${scrollTop}px`;
      sidePanelHeaderEl.style.left = `${scrollLeft}px`;
    }
  }, []);

  function handleHoverOver(idx) {
    const relatedEls = document.querySelectorAll(`[data-rowindex="${idx}"]`);
    if (!isEmptyList(relatedEls)) {
      for (const element of relatedEls) {
        element.classList.add("hovered");
      }
    }
  }
  function handleHoverOut(idx) {
    const relatedEls = document.querySelectorAll(`[data-rowindex="${idx}"]`);
    if (!isEmptyList(relatedEls)) {
      for (const element of relatedEls) {
        element.classList.remove("hovered");
      }
    }
  }

  // NOTE: These are fixed size for now and match with the values in CSS
  // Maybe consider user-settable sizes or based on viewport size
  const colsize = "160px";
  const rowsize = "60px";
  const columnStyle = {
    gridTemplateColumns: `repeat(${timeBuckets.length}, ${colsize})`,
  };

  // Calculate total rows after timeChartItems is created
  const totalRows = timeChartItems?.length || 0;
  const rowStyle = {
    gridTemplateRows: `repeat(${totalRows}, ${rowsize})`,
  };

  // Styles to position the markers
  const nowMarkerStyle = getTimelineMarkerStyle(mNow);

  function handleNowOver(markerClass) {
    const markerEls = Array.from(document.getElementsByClassName(markerClass));
    if (!isEmptyList(markerEls)) {
      markerEls.forEach((el) => {
        el.classList.add("hovered");
      });
    }
  }
  function handleNowOut(markerClass) {
    const markerEls = Array.from(document.getElementsByClassName(markerClass));
    if (!isEmptyList(markerEls)) {
      markerEls.forEach((el) => {
        el.classList.remove("hovered");
      });
    }
  }

  const isItemClickable = !isNullOrUndefined(itemClickHandler);
  const isItemBlockClickable = !isNullOrUndefined(itemBlockClickHandler);

  return (
    <div
      className={`time-chart${size === TIME_CHART_SIZE.LARGE ? " large" : ""}`}
      ref={containerRef}
      onScroll={handleContentScroll}
    >
      <div className="time-chart-header-row">
        <div className="side-panel-header cell-label" ref={sidePanelHeaderRef}>
          <div>{columnTitle}</div>
        </div>
        <div className="time-marker-labels" style={columnStyle}>
          {timeBuckets &&
            timeBuckets.map((i, idx) => {
              const isInNowRange =
                mNow.isAfter(moment(i).add(-15, "minutes")) &&
                mNow.isBefore(moment(i).add(15, "minutes"));
              return (
                <div className="cell" key={`header-${idx}`}>
                  <div>
                    {!isInNowRange &&
                      idx + 1 < timeBuckets.length &&
                      formatTime(i, airportTimezone)}
                  </div>
                </div>
              );
            })}
        </div>
        <div className="time-chart-timeline-markers" style={{ top: 0 }}>
          <div
            className="timeline-marker timeline-marker-now"
            style={nowMarkerStyle}
            onMouseOver={() => {
              handleNowOver("timeline-marker-now");
            }}
            onMouseOut={() => {
              handleNowOut("timeline-marker-now");
            }}
          >
            <div>
              <div className="timeline-marker-label-anchor">
                <div className="timeline-marker-label">
                  <div>
                    <div className="timeline-marker-label-title">
                      {t("now")}
                    </div>
                    <div className="timeline-marker-label-value">
                      {formatTime(mNow, airportTimezone)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="timeline-marker-line"></div>
            </div>
          </div>
        </div>
      </div>
      <div className="time-chart-content">
        <div
          className="time-chart-sidepanel"
          style={rowStyle}
          ref={sidePanelRef}
        >
          {!isEmptyList(timeChartItems) &&
            timeChartItems.map((timeChartItem, idx) => (
              <div
                className={`cell-label${
                  isItemClickable && !isEmptyList(timeChartItem.itemBlocks)
                    ? " clickable"
                    : ""
                }`}
                key={`sidepanel-${idx}`}
                data-rowindex={idx}
                onMouseOver={() => {
                  handleHoverOver(idx);
                }}
                onMouseOut={() => {
                  handleHoverOut(idx);
                }}
                onClick={() => {
                  if (
                    isItemClickable &&
                    !isEmptyList(timeChartItem.itemBlocks)
                  ) {
                    itemClickHandler(timeChartItem);
                  }
                }}
              >
                <div
                  className={`time-chart-item-info${
                    isItemClickable ? " clickable" : ""
                  }`}
                >
                  <div>{timeChartItem.name}</div>
                </div>
              </div>
            ))}
        </div>
        <div className="time-chart-mainpanel">
          <div
            className="time-chart-gridlines"
            style={rowStyle}
            ref={gridlinesRef}
          >
            {!isEmptyList(timeChartItems) &&
              timeChartItems.map((timeChartItem, idx) => (
                <div
                  className="time-chart-gridline"
                  key={`grid-${idx}`}
                  style={columnStyle}
                  data-rowindex={idx}
                  onMouseOver={() => {
                    handleHoverOver(idx);
                  }}
                  onMouseOut={() => {
                    handleHoverOut(idx);
                  }}
                >
                  {timeBuckets &&
                    timeBuckets.map((i, subIdx) => (
                      <div className="gridcell" key={`grid-${idx}-${subIdx}`}>
                        <div data-time-bucket={formatTime(i)}></div>
                      </div>
                    ))}
                </div>
              ))}
          </div>

          <div className="time-chart-timelinebars" style={rowStyle}>
            {!isEmptyList(timeChartItems) &&
              timeChartItems.map((timeChartItem, idx) => (
                <div key={`timelinebar-${idx}`}>
                  {/** Timeline bars **/}
                  {!isEmptyList(timeChartItem.itemBlocks) &&
                    timeChartItem.itemBlocks.map((itemBlock, subIdx) => {
                      const timelineStyle = getTimelineBarStyle(itemBlock);
                      return (
                        <Tooltip
                          key={`timelinebar-task-${idx}-${subIdx}`}
                          title={
                            <div className="tooltip-content">
                              <div className="tooltip-content-heading">
                                <div className="tooltip-content-heading-label">
                                  {itemBlock.title}
                                </div>
                                <div className="tooltip-content-heading-sublabel">
                                  {itemBlock.subtitle}
                                </div>
                              </div>
                              {!isBlank(itemBlock.details) && (
                                <div>{itemBlock.details}</div>
                              )}
                              <div className="tooltip-content-timeranges">
                                <span>
                                  {formatTimeRange(
                                    itemBlock.startTime,
                                    itemBlock.endTime,
                                    airportTimezone
                                  )}
                                </span>
                              </div>
                            </div>
                          }
                          placement="top"
                          componentsProps={tooltipLightStyles}
                          TransitionComponent={Fade}
                          TransitionProps={{ timeout: 500 }}
                          arrow
                        >
                          <div
                            className={`time-chart-timelinebar${
                              isItemBlockClickable ? " clickable" : ""
                            }${
                              isBlank(itemBlock.endLabel) ? " unknown-end" : ""
                            }${timeChartItem.isInactive ? " inactive" : ""} ${
                              itemBlock.variant
                            }`}
                            style={timelineStyle}
                            onClick={() => {
                              if (isItemBlockClickable) {
                                itemBlockClickHandler(itemBlock);
                              }
                            }}
                          >
                            <div className="time-chart-timelinebar-values">
                              <div>
                                {!isNullOrUndefined(itemBlock.mainIcon) && (
                                  <div className="time-chart-timelinebar-icon">
                                    {itemBlock.mainIcon ===
                                    TIME_CHART_ICONS.DEPARTURE ? (
                                      <Departure />
                                    ) : itemBlock.mainIcon ===
                                      TIME_CHART_ICONS.ARRIVAL ? (
                                      <Arrival />
                                    ) : null}
                                  </div>
                                )}
                              </div>
                              <div className="time-chart-timelinebar-value">
                                {itemBlock.startLabel}
                                {!isBlank(itemBlock.endLabel) && (
                                  <>&nbsp;-&nbsp;{itemBlock.endLabel}</>
                                )}
                              </div>
                              <div className="time-chart-timelinebar-value"></div>
                            </div>
                          </div>
                        </Tooltip>
                      );
                    })}
                </div>
              ))}
          </div>
        </div>
      </div>
      <div
        className="time-chart-timeline-markers"
        style={{ top: 0 }}
        ref={markersRef}
      >
        <div
          className="timeline-marker timeline-marker-now"
          style={nowMarkerStyle}
          onMouseOver={() => {
            handleNowOver("timeline-marker-now");
          }}
          onMouseOut={() => {
            handleNowOut("timeline-marker-now");
          }}
        >
          <div>
            <div className="timeline-marker-label-anchor">
              <div className="timeline-marker-label temp-hidden">
                <div>
                  <div className="timeline-marker-label-title">{t("now")}</div>
                  <div className="timeline-marker-label-value">
                    {formatTime(mNow, airportTimezone)}
                  </div>
                </div>
              </div>
            </div>
            <div className="timeline-marker-line"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TimeChart;
